import { type AuthTokens, type OrgJoinPending } from '@apm/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { EmptyState, Skeleton } from '../../components/ui/states';
import { errorMessage } from '../../lib/error-message';
import { orgInviteApi, orgInvitePreviewKey } from '../../features/organizations/api';
import { OrgInviteCard } from '../../features/organizations/components/org-invite-card';
import { apiFetch } from '../../lib/http';

export const Route = createFileRoute('/_app/join/$token')({
  component: JoinOrgPage,
});

/**
 * Nhận lời mời vào tổ chức bằng link — APM-ORG-e.
 *
 * ## Nằm dưới `_app`, tức **đòi đăng nhập** trước
 *
 * Link chỉ cấp tư cách thành viên, không tạo tài khoản. Người chưa đăng nhập
 * sẽ bị layout đẩy sang trang đăng nhập rồi quay lại đây — đúng thứ ta muốn:
 * lời mời phải gắn vào một con người cụ thể, không phải vào ai cầm được link.
 *
 * ## Vì sao KHÔNG tự nhận ngay khi mở trang
 *
 * Mở một URL là thao tác vô tình: link nằm trong lịch sử duyệt, trong bản xem
 * trước của ứng dụng chat, trong trình quét link của email. Gia nhập một tổ
 * chức là việc có hệ quả — nó ghi tên người dùng vào danh sách nhân sự của một
 * công ty — nên nó cần một cú bấm có chủ ý.
 */
function JoinOrgPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = Route.useParams();

  /*
    Xem trước **trước khi** bấm — APM-ORG-e2.

    "Công ty ABC, 4 người" và "Công ty ABC, 400 người" là hai lời mời khác
    nhau. Không có thông tin này thì người dùng bấm gia nhập vào một cái tên.
  */
  const preview = useQuery({
    queryKey: orgInvitePreviewKey(token),
    queryFn: () => orgInviteApi.preview(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      apiFetch<AuthTokens | OrgJoinPending>('/auth/org-invites/accept', {
        method: 'POST',
        body: { token },
      }),
    /*
      Nạp lại cả trang: phiên vừa chuyển sang tổ chức mới, nên mọi dữ liệu đang
      hiện thuộc về chỗ khác. Cùng lý do với `OrgSwitcher`.

      Link cần duyệt (APM-ORGJR-b) thì phiên **không** đổi — ở lại trang này và
      nói rằng đơn đã gửi.
    */
    onSuccess: (result) => {
      if ('status' in result) return;
      window.location.href = '/';
    },
  });

  useEffect(() => {
    // Đổi tiêu đề tab: link này thường mở ở một tab lẫn giữa mười tab khác.
    document.title = t('joinOrg.title');
  }, [t]);

  // Link chết thì nói ngay, không bày ra nút bấm sẽ hỏng.
  if (preview.isError) {
    return <EmptyState title={t('joinOrg.invalidTitle')} description={t('joinOrg.invalidHelp')} />;
  }
  /*
    Lỗi của lượt bấm **không** phải lúc nào cũng là link chết: tài khoản demo,
    hay bị khoá ở tổ chức ấy, đều có câu riêng. Gộp hết thành "link không còn
    hiệu lực" là nói sai cho người đang cầm một link còn tốt.
  */
  if (accept.isError) {
    return (
      <EmptyState title={t('joinOrg.failedTitle')} description={errorMessage(accept.error, t)} />
    );
  }
  if (accept.data && 'status' in accept.data) {
    return (
      <EmptyState
        title={t('joinOrg.pendingTitle')}
        description={t('joinOrg.pendingHelp', { org: accept.data.orgName })}
      />
    );
  }
  if (!preview.data) return <Skeleton className="mx-auto mt-12 h-48 max-w-md" />;

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 py-12">
      {/* Cùng một thẻ với trang đăng nhập và onboarding — APM-ORGP-b: người đi
          qua cả ba trang thấy đúng một lời mời, không phải ba cách kể khác nhau. */}
      <OrgInviteCard preview={preview.data} />

      {/* Nói trước khi bấm: link cần duyệt thì bấm là gửi đơn, không vào ngay. */}
      <p className="text-center text-sm text-muted-foreground">
        {t(preview.data.requiresApproval ? 'joinOrg.helpApproval' : 'joinOrg.help')}
      </p>
      <div className="flex justify-center">
        <Button disabled={accept.isPending} onClick={() => accept.mutate()}>
          {t(preview.data.requiresApproval ? 'joinOrg.request' : 'joinOrg.accept')}
        </Button>
      </div>
    </div>
  );
}
